import * as React from 'react';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';
import cl from 'classnames';

import { numberToABC } from '@doltech/utils/lib/text';

import { useRootElement } from '../../../slatePlugins/common/hocs/current-element.store';
import { colorsV2 } from '../../../figma/colors-v2';
import { ResponsiveTypography } from '../../../figma/Typography/v3/ResponsiveTypography';
import { findBellowByType } from '../../../figma/DolEditor/helpers/nodeValue';
import { useProgressListGrammar } from '../../hocs/progress-list-grammar';
import {
  EditorExerciseMultipleChoiceQuestionEnum,
  EXERCISE_MULTIPLE_CHOICE_OPTIONS,
  EXERCISE_MULTIPLE_CHOICE_QUESTION,
} from '../types';
import { getNodeFromChild } from '../utils/data-mapping';

const OptionText = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 28px;
`;

const OptionCircleText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 28px;
  min-width: 28px;
  height: 28px;

  padding: 2px;
  border-radius: 50%;

  background-color: ${colorsV2.neutral20};
  color: ${colorsV2.neutral140};
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 12px;

  margin-left: -8px;

  padding: 6px 8px;
  background-color: transparent;
  border-radius: 12px;
  transition: all 0.05s ease-out;
  user-select: none;

  .option-text {
    color: ${colorsV2.neutral180};
  }

  .option-circle-text {
    transition: all 0.05s ease-out;

    p {
      color: inherit;
    }
  }

  &:not(.readonly) {
    cursor: pointer;

    &:hover {
      background-color: ${colorsV2.neutral10};

      .option-circle-text {
        background-color: ${colorsV2.neutral140};
        color: ${colorsV2.white100};
      }
    }

    &.selected {
      background-color: ${colorsV2.blue10};

      .option-circle-text {
        background-color: ${colorsV2.blue100};
        color: ${colorsV2.white100};
      }
    }
  }

  &.correct {
    background-color: ${colorsV2.green10};

    .option-circle-text {
      background-color: ${colorsV2.green100};
      color: ${colorsV2.white100};
    }

    .option-correction {
      color: ${colorsV2.green100};
    }
  }

  &.wrong {
    background-color: ${colorsV2.red10};

    .option-circle-text {
      background-color: ${colorsV2.red100};
      color: ${colorsV2.white100};
    }

    .option-correction {
      color: ${colorsV2.red100};
    }
  }

  &.missed {
    background-color: ${colorsV2.neutral10};

    .option-circle-text {
      background-color: ${colorsV2.neutral140};
      color: ${colorsV2.white100};
    }

    .option-correction {
      color: ${colorsV2.neutral100};
    }
  }
`;

export const MultipleChoiceOptionItem = (props: RenderElementProps) => {
  const { element, children, attributes } = props;
  const progressStore = useProgressListGrammar();
  const root = useRootElement();

  const optionId = element.id;
  const optionsNodeList = findBellowByType(root, EXERCISE_MULTIPLE_CHOICE_OPTIONS);
  const questionNodeList = findBellowByType(root, EXERCISE_MULTIPLE_CHOICE_QUESTION);

  const optionsNode = getNodeFromChild(optionsNodeList, optionId);
  const questionNode = getNodeFromChild(questionNodeList, optionsNode?.id);

  const options = optionsNode.children;
  const currentOptionIndex = options?.findIndex(eachOption => eachOption?.id === optionId);

  const correctOptions = options?.filter((eachOption) => eachOption.isCorrect);
  const correctOptionIds = correctOptions?.map((eachCorrectOption) => eachCorrectOption?.id);

  const currentAnswer = progressStore?.userAnswers?.find(eachAnswer => eachAnswer?.questionId === questionNode?.id);
  const currentAnswerIds = currentAnswer?.answerIds || [];

  const selected = currentAnswerIds?.includes(optionId);
  const hasSelected = Boolean(currentAnswerIds?.length);
  const isCorrectOption = correctOptionIds?.includes(optionId);

  let status = 'default';
  let correctionText = '';

  if (progressStore?.isShowResult) {
    if (selected) {
      status = isCorrectOption ? 'correct' : 'wrong';
      correctionText = isCorrectOption ? 'Đúng' : 'Sai';
    } else if ((!hasSelected || correctOptionIds?.length > 1) && isCorrectOption) {
      status = 'missed';
      correctionText = 'Missed';
    }

  }

  const handleUpdateUserAnswerById = ({ answerIds }: any) => {
    progressStore?.setUserAnswers((prevUserAnswers) => {
      if (prevUserAnswers?.find(eachAnswer => eachAnswer?.questionId === questionNode?.id)) {
        return prevUserAnswers?.map(eachAnswer => {
          if (questionNode?.id === eachAnswer?.questionId) {
            return {
              questionId: questionNode?.id,
              answerIds,
            };
          }
          return eachAnswer;
        });
      }
      return [
        ...prevUserAnswers,
        {
          questionId: questionNode?.id,
          answerIds,
        },
      ];
    });
  };

  const handleOptionChange = (inputValue) => {
    if (currentAnswerIds?.includes(inputValue)) {
      const answerIds = currentAnswerIds.filter((id) => id !== inputValue);
      handleUpdateUserAnswerById({ answerIds });

    } else {
      handleUpdateUserAnswerById({
        answerIds: [...currentAnswerIds, inputValue],
      });
    }
  };

  const handleSelect = () => {
    if (EditorExerciseMultipleChoiceQuestionEnum.MULTIPLE_ANSWER === questionNode?.questionType) {
      handleOptionChange(optionId);
    }
    if (EditorExerciseMultipleChoiceQuestionEnum.SINGLE_ANSWER === questionNode?.questionType) {
      handleUpdateUserAnswerById({ answerIds: [optionId] });
    }
  };

  return (
    <Container
      className={cl('multiple-choice-option-item', status, {
        selected,
        'readonly': progressStore?.isShowResult,
      })}
      onClick={() => !progressStore?.isShowResult && handleSelect()}
      contentEditable={false}
      {...attributes}
    >
      <OptionCircleText className="option-circle-text">
        <ResponsiveTypography.Paragraph variant="semi-bold/12-20" uppercase>
          {numberToABC(currentOptionIndex + 1)}
        </ResponsiveTypography.Paragraph>
      </OptionCircleText>
      <OptionText className="option-text">
        {children}
      </OptionText>
      {status !== 'default' && (
        <ResponsiveTypography.Paragraph className="option-correction" variant="medium/14-20">
          {correctionText}
        </ResponsiveTypography.Paragraph>
      )}
    </Container>
  );
};
